import React, { useMemo } from "react";
import { Box, Typography, Stack, Chip, Divider } from "@mui/material";

export default function ProductVariants({
  variants = [],
  attributes = [],
  selectedVariant,
  onSelectVariant,
}) {
  // Group unique feature values by feature name.
  const featureGroups = useMemo(() => {
    const groups = {};

    variants.forEach((variant) => {
      variant.features?.forEach((feature) => {
        if (!feature.name || !feature.value) return;

        if (!groups[feature.name]) {
          groups[feature.name] = new Set();
        }

        groups[feature.name].add(feature.value);
      });
    });

    return Object.fromEntries(
      Object.entries(groups).map(([name, values]) => [
        name,
        Array.from(values),
      ]),
    );
  }, [variants]);

  // Build the selected feature map.
  const currentSelections = useMemo(() => {
    const selections = {};

    selectedVariant?.features?.forEach((feature) => {
      if (feature.name && feature.value) {
        selections[feature.name] = feature.value;
      }
    });

    return selections;
  }, [selectedVariant]);

  const featureKeys = Object.keys(featureGroups);
  const hasSelectableFeatures = variants.length > 0 && featureKeys.length > 0;
  const hasStaticAttributes = attributes && attributes.length > 0;

  // Render nothing only if BOTH variants and static attributes are empty
  if (!hasSelectableFeatures && !hasStaticAttributes) {
    return null;
  }

  const isVariantAvailable = (variant) => {
    const stock = Math.max(0, Number(variant?.stock ?? 0));

    const packageSize = Math.max(1, Number(variant?.units_per_package ?? 1));

    return stock >= packageSize;
  };
  const isVariantCompatible = (variant, selections) => {
    if (!isVariantAvailable(variant)) return false;

    return Object.entries(selections).every(([featureName, featureValue]) =>
      variant.features?.some(
        (feature) =>
          feature.name === featureName && feature.value === featureValue,
      ),
    );
  };

  const getValueStatus = (featureName, featureValue) => {
    const targetSelections = {
      ...currentSelections,
      [featureName]: featureValue,
    };

    const hasCompatibleVariant = variants.some((variant) =>
      isVariantCompatible(variant, targetSelections),
    );

    const existsInAnyVariant = variants.some((variant) =>
      variant.features?.some(
        (feature) =>
          feature.name === featureName && feature.value === featureValue,
      ),
    );

    return {
      isCompatible: hasCompatibleVariant,
      isOutOfStock: existsInAnyVariant && !hasCompatibleVariant,
    };
  };

  // Continue directly with this function:
  const handleFeatureSelect = (featureName, featureValue) => {
    const targetSelections = {
      ...currentSelections,
      [featureName]: featureValue,
    };

    const matchedVariant = variants.find((variant) =>
      isVariantCompatible(variant, targetSelections),
    );

    if (matchedVariant) {
      onSelectVariant?.(matchedVariant);
    }
  };

  return (
    <Box mb={1}>
      {/* 1. NON-SELECTABLE STATIC ATTRIBUTES */}
      {hasStaticAttributes && (
        <Box
          mb={hasSelectableFeatures ? 3 : 1}
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={800}
            mb={2}
            color="text.primary"
          >
            مشخصات کلی
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
            }}
          >
            {attributes.map((attr, idx) => (
              <Chip
                key={`attr-${attr.feature_id}-${idx}`}
                label={`${attr.name}: ${attr.value}`}
                variant="outlined"
                sx={{
                  maxWidth: "100%",
                  minWidth: 0,
                  height: "auto",
                  boxSizing: "border-box",
                  fontWeight: 700,
                  borderRadius: "6px",
                  borderColor: "grey.300",
                  bgcolor: "grey.50",
                  color: "text.secondary",
                  "& .MuiChip-label": {
                    display: "block",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    py: 0.75,
                    minWidth: 0,
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Divider if both sections exist */}
      {hasStaticAttributes && hasSelectableFeatures && (
        <Divider
          sx={{ mb: 3, borderStyle: "dashed", borderColor: "grey.300" }}
        />
      )}

      {/* 2. SELECTABLE FEATURES / VARIANTS */}
      {hasSelectableFeatures && (
        <>
          {variants.length === 1 ? (
            <Box mb={1}>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                mb={2}
                color="text.primary"
              >
                ویژگی‌های محصول
              </Typography>
              {featureKeys.map((featureName) => {
                const value = featureGroups[featureName][0];
                return (
                  <Box key={featureName} mb={2}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      mb={1}
                      color="text.secondary"
                    >
                      {featureName}:
                    </Typography>
                    <Chip
                      label={value}
                      variant="outlined"
                      sx={{
                        fontWeight: 700,
                        borderRadius: "6px",
                        borderColor: "grey.400",
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box mb={1}>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                mb={2}
                color="text.primary"
              >
                انتخاب مشخصات
              </Typography>

              {featureKeys.map((featureName) => (
                <Box key={featureName} mb={2.5}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    mb={1.5}
                    color="text.secondary"
                  >
                    {featureName}:{" "}
                    {currentSelections[featureName] && (
                      <Typography
                        component="span"
                        variant="subtitle2"
                        color="text.primary"
                        fontWeight={800}
                      >
                        {currentSelections[featureName]}
                      </Typography>
                    )}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {featureGroups[featureName].map((value) => {
                      const isSelected =
                        currentSelections[featureName] === value;
                      const { isCompatible, isOutOfStock } = getValueStatus(
                        featureName,
                        value,
                      );

                      return (
                        <Chip
                          key={`${featureName}-${value}`}
                          label={value}
                          disabled={isOutOfStock}
                          onClick={() =>
                            !isOutOfStock &&
                            handleFeatureSelect(featureName, value)
                          }
                          sx={{
                            fontWeight: 700,
                            px: 1,
                            borderRadius: "6px",
                            borderStyle:
                              !isSelected && !isCompatible ? "dashed" : "solid",
                            borderWidth: "1px",
                            borderColor: isSelected
                              ? "#8257ac"
                              : isCompatible
                                ? "grey.400"
                                : "grey.300",
                            bgcolor: isSelected ? "#8257ac" : "transparent",
                            color: isSelected
                              ? "#fff"
                              : isOutOfStock
                                ? "text.disabled"
                                : "text.primary",
                            opacity: isSelected
                              ? 1
                              : isCompatible
                                ? 1
                                : isOutOfStock
                                  ? 0.3
                                  : 0.55,
                            transition: "all 0.2s",
                            "&:hover": {
                              bgcolor: isSelected
                                ? "#6f449a"
                                : isOutOfStock
                                  ? "transparent"
                                  : "grey.100",
                              borderColor: isSelected
                                ? "#6f449a"
                                : isOutOfStock
                                  ? "grey.300"
                                  : "#8257ac",
                              opacity: 1,
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
